/**
 * Cliente API para el microservicio DentalPro
 * Tokens en cookies. Sesión de carrito: cookie HttpOnly del API + respaldo X-Cart-Session en el front.
 */
import axios from 'axios';
import {
  getToken,
  removeToken,
  clearAllAuth,
  COOKIE_KEYS,
  getCartSessionId,
  setCartSessionId,
} from '../utils/cookies';
import { getApiV1Base } from '../utils/apiBase';

const api = axios.create({
  baseURL: getApiV1Base(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/** Token de cliente recién emitido (hasta que la cookie esté disponible). */
let pendingCustomerToken = null;

/** Evita que un 401 tardío (sesión vieja) borre el token recién emitido en /login. */
let authLoginInProgress = false;

export function setAuthLoginInProgress(value) {
  authLoginInProgress = !!value;
}

export function isAuthLoginInProgress() {
  return authLoginInProgress;
}

/** Rutas que exigen JWT de cliente (consultorio / tienda), nunca token admin. */
const CUSTOMER_ONLY_URL_PARTS = [
  '/consultorio-clinical',
  '/consultorio-profile',
  '/consultorio-admin',
  '/consultorio-members',
  '/dental-charts',
  '/audit-logs/consultorio',
  '/auth/me/customer',
  '/auth/change-password',
  '/cart',
  '/orders',
  '/speech',
];

export function setPendingCustomerToken(token) {
  pendingCustomerToken = token && typeof token === 'string' ? token : null;
}

function isCustomerOnlyApiUrl(url) {
  const u = normalizeRequestUrl(url);
  return CUSTOMER_ONLY_URL_PARTS.some((part) => u.includes(part));
}

function normalizeRequestUrl(requestUrl) {
  const raw = String(requestUrl || '');
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return new URL(raw).pathname;
    }
  } catch {
    /* usar raw */
  }
  return raw;
}

function isCartApiUrl(requestUrl) {
  return normalizeRequestUrl(requestUrl).includes('/cart');
}

/** Elige el JWT correcto según endpoint y zona de la app. */
function resolveBearerToken(requestUrl) {
  const url = normalizeRequestUrl(requestUrl);
  const adminToken = getToken(COOKIE_KEYS.ADMIN_TOKEN);
  const authToken = getToken(COOKIE_KEYS.AUTH_TOKEN) || pendingCustomerToken;
  const path = typeof window !== 'undefined' ? window.location.pathname : '';

  if (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/company/contact')
  ) {
    return null;
  }

  if (url.includes('/auth/me/admin')) {
    return adminToken;
  }
  if (url.includes('/auth/me/customer')) {
    return authToken;
  }

  // Zona admin antes de rutas "solo cliente" (p. ej. /orders también las usa Compras).
  if (path.startsWith('/admin')) {
    return adminToken;
  }

  if (isCustomerOnlyApiUrl(url) || path.startsWith('/consultorio') || path === '/cambiar-contrasena') {
    return authToken || null;
  }
  if (authToken) {
    return authToken;
  }
  return adminToken;
}

// Interceptor para añadir token JWT (desde cookies)
api.interceptors.request.use((config) => {
  config.baseURL = getApiV1Base();
  if (!config.headers.Authorization) {
    const tokenToUse = resolveBearerToken(config.url);
    if (tokenToUse) {
      config.headers.Authorization = `Bearer ${tokenToUse}`;
    }
  }
  // Sesión de carrito invitado: el front y el API viven en puertos distintos;
  // la cookie HttpOnly del API a veces no se reutiliza, así que mandamos el header.
  if (isCartApiUrl(config.url)) {
    const cartSession = getCartSessionId();
    if (cartSession && !config.headers['X-Cart-Session']) {
      config.headers['X-Cart-Session'] = cartSession;
    }
  }
  return config;
});

// Interceptor para manejar 401 (sesión expirada). No redirigir en intentos de login fallidos.
api.interceptors.response.use(
  (res) => {
    const sessionFromHeader = res.headers?.['x-cart-session'];
    if (sessionFromHeader) {
      setCartSessionId(sessionFromHeader);
    }
    return res;
  },
  (err) => {
    if (err.response?.status === 401) {
      const requestUrl = String(err.config?.url || '');
      const isAuthLoginRequest =
        requestUrl.includes('/auth/login/admin') ||
        requestUrl.includes('/auth/login/customer') ||
        requestUrl.includes('/auth/register');

      if (isAuthLoginRequest) {
        return Promise.reject(err);
      }

      const path = window.location.pathname;
      const isAdminArea = path.startsWith('/admin');
      const isOnAdminLogin = path === '/admin/login';

      const isConsultorioArea = path.startsWith('/consultorio');
      const isOnLogin = path === '/login' || path === '/registro';
      const isOnCambiarContrasena = path === '/cambiar-contrasena';

      if (isAdminArea && !isOnAdminLogin) {
        const hadAdminAuth = !!getToken(COOKIE_KEYS.ADMIN_TOKEN);
        const sentAuth = !!err.config?.headers?.Authorization;
        if (hadAdminAuth && sentAuth && !authLoginInProgress) {
          removeToken(COOKIE_KEYS.ADMIN_TOKEN);
          window.location.href = '/admin/login';
        }
      } else if (isConsultorioArea && !isOnLogin) {
        clearAllAuth();
        pendingCustomerToken = null;
        window.location.href = '/login';
      } else if (
        (isOnLogin || isOnCambiarContrasena) &&
        getToken(COOKIE_KEYS.AUTH_TOKEN) &&
        !authLoginInProgress &&
        requestUrl.includes('/auth/me/')
      ) {
        /* Solo sesión caducada al restaurar; no tocar token nuevo mientras login() corre */
        clearAllAuth();
        pendingCustomerToken = null;
      }
    }
    return Promise.reject(err);
  }
);

export default api;
